import { createContext, useContext, type ReactNode } from 'react';

export interface IaConnectionView {
  id: string;
  uid: string;
  email: string | null;
  clientName: string;
  connectedAt: Date | null;
  lastSeenAt: Date | null;
  revokedAt: Date | null;
}

/** Un modele proposable pour Lia ; la liste vient de l'hote (`LIA_MODEL_CHOICES` du SDK). */
export interface IaModelChoiceView {
  id: string;
  label: string;
  price: string;
  speed: string;
  hosts: string;
}

export type LiaRecordStatus = 'active' | 'archived' | 'deleted' | 'missing' | 'inaccessible' | 'unknown';

/** Le lien WhatsApp de la personne connectee, tel que l'ecran le montre. */
export interface WhatsappLienView {
  phone: string;
  numeroMasque: string;
  notifications: boolean;
  linkedAt: Date | null;
}

export interface WhatsappAppairageView { code: string; numeroLia: string; lienWaMe: string; expiresAt: Date }

/** Lia par WhatsApp : ce que Parametres > IA en montre. Absent : pas de WhatsApp sur cet hote. */
export interface WhatsappPort {
  ready: boolean;
  lien: WhatsappLienView | null;
  numeroLia: string;
  /** Le numero Lia en groupes lisibles. */
  numeroLiaLisible: string;
  appairer(): Promise<WhatsappAppairageView>;
  delier(): Promise<void>;
  setNotificationsPersonnelles(notifications: boolean): Promise<void>;
}

/**
 * Un CONNECTEUR du membre (Google Agenda, Gmail, Outlook Agenda, Outlook Mail) :
 * une connexion OAuth de la brique Connexions du socle (`orgs/{orgId}/connexions`,
 * fournisseur `oauth2` du registre), telle que Parametres > Conversations la
 * montre. Jamais de jeton ici.
 */
export interface ConnecteurView {
  id: string;
  /** `google_agenda`, `gmail`, `outlook_agenda`, `outlook_mail` (registre `@taki/data-model`, `connexions.ts`). */
  fournisseur: string;
  nom: string;
  /** L'adresse du compte chez le fournisseur. */
  compte: string;
  acces: string;
  usage: 'lia' | 'lia_flux';
  statut: 'active' | 'a_renouveler' | 'erreur';
  /** Agendas ou dossiers inclus ; vide = le principal (agenda) ou tous (mail). */
  selection: readonly { id: string; nom: string }[];
  derniereUtilisation: Date | null;
}

export interface ConnecteurReglagesView {
  nom?: string;
  acces?: string;
  selection?: string[];
  usage?: 'lia' | 'lia_flux';
}

/** Les connecteurs du membre et leurs gestes. Absent : l'hote ne les propose pas. */
export interface ConnecteursPort {
  ready: boolean;
  connexions: readonly ConnecteurView[];
  /** Les fournisseurs dont les identifiants OAuth sont poses dans cet environnement ; `null` tant qu'on ne sait pas. */
  configures: ReadonlySet<string> | null;
  /** Part chez le fournisseur (le navigateur quitte la page) ; `connexionId` = reconnecter cette connexion. */
  connecter(fournisseur: string, options?: { acces?: string; connexionId?: string }): Promise<void>;
  /** Applique les reglages ; un niveau qui demande des droits en plus part chez le fournisseur. */
  regler(id: string, reglages: ConnecteurReglagesView): Promise<void>;
  /** Les agendas (agenda) ou dossiers et libelles (mail) du compte, lus chez le fournisseur. */
  choix(id: string): Promise<{ id: string; nom: string; principal: boolean }[]>;
  /** Revoque chez le fournisseur (quand il le permet) et efface la connexion. */
  retirer(id: string): Promise<{ revoque: boolean }>;
}

export interface IaSettingsPort {
  enabled: boolean;
  launcher: 'mascot' | 'bubble';
  /** Modele de Lia choisi par l'org, `auto` pour Jev, `null` pour le defaut du projet. */
  model: string | null;
  /**
   * Etat actuel d'une fiche citee par Lia, relu avec les droits du membre : une
   * carte vers une fiche supprimee ne rouvre pas un fantome. Absent : inconnu.
   */
  recordStatus?(family: string, id: string): Promise<LiaRecordStatus>;
  models: readonly IaModelChoiceView[];
  defaultModel: string;
  ready: boolean;
  canManage: boolean;
  setEnabled(enabled: boolean): Promise<void>;
  setLauncher(launcher: IaSettingsPort['launcher']): Promise<void>;
  setModel(model: string | null): Promise<void>;
  connections(includeAll?: boolean): Promise<IaConnectionView[]>;
  revokeConnection(id: string): Promise<void>;
  /** Lia par WhatsApp, si l'hote le propose. */
  whatsapp?: WhatsappPort | undefined;
  /** Les connecteurs mail et agenda du membre, si l'hote les propose. */
  connecteurs?: ConnecteursPort | undefined;
}

const Context = createContext<IaSettingsPort | null>(null);

export function IaSettingsProvider({ value, children }: { value: IaSettingsPort; children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useIaSettingsPort(): IaSettingsPort | null {
  return useContext(Context);
}
